import { Box, makeStyles } from '@material-ui/core';
import React, { useMemo, useRef, useState, useCallback } from 'react';
import {
    format,
    addDays,
    startOfToday,
    isSameDay,
    isBefore,
    eachDayOfInterval,
    differenceInDays,
    parseISO,
} from 'date-fns';
import ClinicianTimeSelectionContainer from './ClinicianTimeSelectionContainer';

const useStyles = makeStyles(theme => ({
    root: {
        width: '100%',
    },
    dateCard: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 16,
        padding: 20,
        marginBottom: 20,
        backgroundColor: theme.palette.background.paper,
    },
    header: {
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        marginBottom: 16,
    },
    headerTitle: {
        fontSize: 24,
        fontWeight: 700,
        [theme.breakpoints.down('sm')]: {
            fontSize: 22,
        },
    },
    headerSubtitle: {
        fontSize: 14,
        color: theme.palette.text.secondary,
    },
    dateStripWrapper: {
        width: '100%',
    },
    dateStrip: {
        display: 'flex',
        gap: 8,
        overflowX: 'auto',
        overflowY: 'hidden',
        paddingBottom: 8,
        scrollbarWidth: 'none',
        '-ms-overflow-style': 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        '-webkit-overflow-scrolling': 'touch',
    },
    datePill: {
        flexShrink: 0,
        width: 60,
        height: 76,
        borderRadius: 16,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.default,
        transition: 'all 0.15s ease',
        position: 'relative',
        '&:hover': {
            borderColor: theme.palette.primary.main,
        },
    },
    datePillSelected: {
        backgroundColor: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        '& $datePillDay': { color: theme.palette.primary.contrastText },
        '& $datePillNum': { color: theme.palette.primary.contrastText },
    },
    datePillUnavailable: {
        opacity: 0.4,
    },
    datePillDay: {
        fontSize: 10,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: theme.palette.text.secondary,
        marginBottom: 2,
    },
    datePillNum: {
        fontSize: 18,
        fontWeight: 700,
        color: theme.palette.text.primary,
    },
    datePillDot: {
        position: 'absolute',
        bottom: 8,
        width: 4,
        height: 4,
        borderRadius: '50%',
        backgroundColor: theme.palette.primary.main,
    },
    selectedDateLabel: {
        fontSize: 24,
        fontWeight: 700,
        color: theme.palette.text.primary,
        marginBottom: 8,
        textAlign: 'left',
    },
    loadingAvailability: {
        fontStyle: 'italic',
        fontSize: 16,
        color: theme.palette.text.secondary,
        margin: '42px 0',
    },
    noAvailability: {
        textAlign: 'center',
        padding: '48px 24px',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 16,
    },
    noAvailTitle: {
        fontSize: 18,
        fontWeight: 700,
        marginBottom: 8,
    },
    noAvailText: {
        fontSize: 14,
        color: theme.palette.text.secondary,
        marginBottom: 16,
    },
    nextAvailButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '12px 24px',
        borderRadius: 24,
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        fontWeight: 700,
        fontSize: 14,
        border: 'none',
        cursor: 'pointer',
        '&:hover': {
            filter: 'brightness(0.9)',
        },
    },
    clinicianCardWrapper: {
        width: '100%',
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: 16,
        alignItems: 'start',
        [theme.breakpoints.up('md')]: {
            gridTemplateColumns: '1fr 1fr',
        },
    },
    clinicianCard: {
        borderRadius: 16,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        width: '100%',
        boxSizing: 'border-box',
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
    },
    clinicianCardContent: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
        gap: 12,
        marginBottom: 16,
    },
    clinicianIcon: {
        borderRadius: '50%',
        width: 56,
        minWidth: 56,
        height: 56,
        overflow: 'hidden',
        backgroundColor: theme.palette.action.hover,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 18,
        fontWeight: 700,
        color: theme.palette.text.disabled,
        border: `1px solid ${theme.palette.divider}`,
    },
    clinicianImage: {
        height: 56,
        width: 56,
        objectFit: 'cover',
    },
    clinicianInfo: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    clinicianTitle: {
        fontWeight: 700,
        fontSize: 15,
        color: theme.palette.text.primary,
    },
    clinicianSex: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        fontWeight: 500,
    },
}));

function getTimezoneAbbreviation(timezone) {
    const date = new Date();
    const formatted = date.toLocaleString('en-US', {
        timeZone: timezone,
        timeZoneName: 'short',
    });

    // Extract the timezone abbreviation from the formatted string
    // Format is typically: "10/10/2025, 3:45:12 PM EDT"
    const match = formatted.match(/\s([A-Z]{3,5})$/);
    return match ? match[1] : timezone;
}

function filterQhpsWithAvailability(qhps) {
    return qhps.filter(qhp => {
        return !Object.values(qhp.availability_by_date).every(date => date.length === 0);
    });
}

const DEFAULT_DATE_STRIP_DAYS = 31;

export default function ClinicianContainer({
    availabilityByClinician,
    onVisitTimeSelected,
    onBookVisit,
    timezone,
}) {
    const today = useMemo(() => startOfToday(), []);
    const dateCount = useMemo(() => {
        if (!availabilityByClinician) return DEFAULT_DATE_STRIP_DAYS;
        let latestDate = null;
        for (const qhp of availabilityByClinician) {
            for (const dk of Object.keys(qhp?.availability_by_date ?? {})) {
                const d = parseISO(dk);
                if (!latestDate || d > latestDate) latestDate = d;
            }
        }
        if (!latestDate) return DEFAULT_DATE_STRIP_DAYS;
        return differenceInDays(latestDate, today);
    }, [availabilityByClinician, today]);
    const classes = useStyles();
    const dates = useMemo(
        () => eachDayOfInterval({ start: today, end: addDays(today, dateCount) }),
        [today, dateCount]
    );
    const [selectedDate, setSelectedDate] = useState(() => startOfToday());
    const [activeSelection, setActiveSelection] = useState(null); // { clinicianId, time }

    const dateStripRef = useRef(null);

    const scrollToCenter = useCallback((el, index) => {
        const strip = dateStripRef.current;
        if (!strip) return;
        if (!el && index != null) {
            el = strip.children[index];
        }
        if (!el) return;
        const stripRect = strip.getBoundingClientRect();
        const pillRect = el.getBoundingClientRect();
        const scrollLeft =
            strip.scrollLeft +
            (pillRect.left - stripRect.left) -
            stripRect.width / 2 +
            pillRect.width / 2;
        strip.scrollTo({ left: scrollLeft, behavior: 'smooth' });
    }, []);

    const handleDateSelect = (date, el, index) => {
        setSelectedDate(date);
        setActiveSelection(null);
        scrollToCenter(el, index);
    };

    const clinicians = useMemo(() => {
        if (!availabilityByClinician) return null;
        return filterQhpsWithAvailability(availabilityByClinician);
    }, [availabilityByClinician]);

    const dateKey = format(selectedDate, 'yyyy-MM-dd');

    // Filter to clinicians with availability on selected date,
    // then sort by: (a) earliest slot that day, (b) most slots that day
    const sortedClinicians = useMemo(() => {
        if (!clinicians) return null;
        return [...clinicians]
            .filter(c => (c.availability_by_date[dateKey] || []).length > 0)
            .sort((a, b) => {
                const aTimes = [...(a.availability_by_date[dateKey] || [])].sort();
                const bTimes = [...(b.availability_by_date[dateKey] || [])].sort();
                // First: earliest appointment
                const cmp = (aTimes[0] || '').localeCompare(bTimes[0] || '');
                if (cmp !== 0) return cmp;
                // Then: most available (descending)
                return bTimes.length - aTimes.length;
            });
    }, [clinicians, dateKey]);

    // Check if any clinician has availability on selected date
    const hasAnyAvailability = useMemo(() => {
        return sortedClinicians && sortedClinicians.length > 0;
    }, [sortedClinicians]);

    // Find next date with availability (searches all availability keys, not just the strip)
    const nextAvailableDate = useMemo(() => {
        if (hasAnyAvailability || !clinicians) return null;
        // Collect all dates with availability across all clinicians
        const allAvailDates = new Set();
        for (const c of clinicians) {
            for (const [d, slots] of Object.entries(c.availability_by_date)) {
                if (slots.length > 0) allAvailDates.add(d);
            }
        }
        // Find the earliest date after selectedDate
        const sorted = [...allAvailDates].sort();
        const selectedKey = format(selectedDate, 'yyyy-MM-dd');
        for (const dk of sorted) {
            if (dk > selectedKey) {
                // Parse as local date (yyyy-MM-dd)
                const [y, m, d] = dk.split('-').map(Number);
                return new Date(y, m - 1, d);
            }
        }
        return null;
    }, [selectedDate, hasAnyAvailability, clinicians]);

    const handleSlotSelect = (time, clinician) => {
        setActiveSelection({ clinicianId: clinician.uuid, time });
        onVisitTimeSelected(time, clinician);
    };

    // Check which dates have any availability across all clinicians
    const datesWithAvailability = useMemo(() => {
        if (!clinicians) return new Set();
        const available = new Set();
        for (const c of clinicians) {
            for (const [d, slots] of Object.entries(c.availability_by_date)) {
                if (slots.length > 0) available.add(d);
            }
        }
        return available;
    }, [clinicians]);

    const timezoneLabel =
        availabilityByClinician && timezone
            ? `All times in ${getTimezoneAbbreviation(timezone)}`
            : '';

    const isLoading = !sortedClinicians;

    return (
        <Box className={classes.root}>
            {/* Date card */}
            <Box className={classes.dateCard}>
                <Box className={classes.header}>
                    <span className={classes.headerTitle}>When?</span>
                    {timezoneLabel && (
                        <span className={classes.headerSubtitle}>{timezoneLabel}</span>
                    )}
                </Box>
                <Box className={classes.dateStripWrapper}>
                    <Box className={classes.dateStrip} ref={dateStripRef}>
                        {dates.map(date => {
                            const isSelected = isSameDay(date, selectedDate);
                            const dk = format(date, 'yyyy-MM-dd');
                            const hasAvail = !isLoading && datesWithAvailability.has(dk);
                            return (
                                <Box
                                    key={dk}
                                    className={[
                                        classes.datePill,
                                        isSelected ? classes.datePillSelected : '',
                                        !isLoading && !hasAvail && !isSelected
                                            ? classes.datePillUnavailable
                                            : '',
                                    ]
                                        .filter(Boolean)
                                        .join(' ')}
                                    onClick={e => handleDateSelect(date, e.currentTarget)}
                                >
                                    <span className={classes.datePillDay}>
                                        {format(date, 'EEE')}
                                    </span>
                                    <span className={classes.datePillNum}>{format(date, 'd')}</span>
                                    {hasAvail && !isSelected && (
                                        <span className={classes.datePillDot} />
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Box>

            {/* Selected date label */}
            <Box className={classes.selectedDateLabel}>{format(selectedDate, 'EEEE, MMMM do')}</Box>

            {/* Clinician cards */}
            {isLoading ? (
                <Box className={classes.noAvailability}>
                    <div className={classes.loadingAvailability}>Loading availability</div>
                </Box>
            ) : !hasAnyAvailability ? (
                <Box className={classes.noAvailability}>
                    <div className={classes.noAvailTitle}>No availability on this date</div>
                    <div className={classes.noAvailText}>
                        Try selecting another date or check the next available opening.
                    </div>
                    {nextAvailableDate && (
                        <button
                            className={classes.nextAvailButton}
                            onClick={() => {
                                const idx = dates.findIndex(d => isSameDay(d, nextAvailableDate));
                                handleDateSelect(nextAvailableDate, null, idx);
                            }}
                        >
                            Go to {format(nextAvailableDate, 'MMMM do')} &rarr;
                        </button>
                    )}
                </Box>
            ) : (
                <Box className={classes.clinicianCardWrapper}>
                    {sortedClinicians.map(avail => (
                        <ClinicianCard
                            key={avail.qhp.uuid}
                            clinician={avail.qhp}
                            availability={avail.availability_by_date}
                            selectedDate={selectedDate}
                            activeSelection={activeSelection}
                            onSlotSelect={handleSlotSelect}
                            onBookVisit={onBookVisit}
                            timezone={timezone}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
}

function ClinicianCard({
    clinician,
    availability,
    selectedDate,
    activeSelection,
    onSlotSelect,
    onBookVisit,
    timezone,
}) {
    const classes = useStyles();
    const initials = `${clinician.first_name.charAt(0)}${clinician.last_name.charAt(0)}`;

    return (
        <Box className={classes.clinicianCard}>
            <Box className={classes.clinicianCardContent}>
                <div className={classes.clinicianIcon}>
                    {clinician.logo_url ? (
                        <img
                            src={clinician.logo_url}
                            alt={`${clinician.first_name} ${clinician.last_name}`}
                            className={classes.clinicianImage}
                        />
                    ) : (
                        <span>{initials}</span>
                    )}
                </div>
                <div className={classes.clinicianInfo}>
                    <div className={classes.clinicianTitle}>
                        {clinician.first_name} {clinician.last_name}
                    </div>
                    <div className={classes.clinicianSex}>Sex: {clinician.sex}</div>
                </div>
            </Box>
            <ClinicianTimeSelectionContainer
                clinician={clinician}
                availability={availability}
                selectedDate={selectedDate}
                activeSelection={activeSelection}
                onSlotSelect={onSlotSelect}
                onBookVisit={onBookVisit}
                timezone={timezone}
            />
        </Box>
    );
}
